/**
 * useElapsedTimer — Matches Xamarin DashboardPageViewModel Timer_Elapsed
 *
 * Calculates elapsed seconds from TaskStartDateTime and ticks every second.
 * Survives page refresh because it always recalculates from the persisted
 * TaskStartDateTime (from the API response), not from an incrementing counter.
 *
 * Xamarin: DateTime.Now.Subtract(Convert.ToDateTime(InprogressTask.TaskStartedDateTime))
 */

import { useEffect, useRef, useState } from 'react';

import type { TaskDetail } from '@/types';

function computeElapsed(startDateTime: string): number {
  // ISO strings with timezone (e.g. "...Z" or "+05:30") — set locally when task starts
  if (/[Zz]|[+-]\d{2}:\d{2}/.test(startDateTime)) {
    const start = Date.parse(startDateTime);
    if (isNaN(start)) return 0;
    return Math.max(0, Math.floor((Date.now() - start) / 1000));
  }

  // OIC API returns Task_start_datetime without timezone suffix
  // (e.g. "2026-06-16 00:14:00"). Convert Date.now() to Pacific
  // time so both sides are in the same timezone.
  const nowPST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const startPST = new Date(startDateTime);
  if (isNaN(startPST.getTime())) return 0;
  return Math.max(0, Math.floor((nowPST.getTime() - startPST.getTime()) / 1000));
}

export function useElapsedTimer(task: TaskDetail | null | undefined): number {
  const [elapsed, setElapsed] = useState(() =>
    task?.TaskStartDateTime ? computeElapsed(task.TaskStartDateTime) : 0
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Clear any previous interval
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!task?.TaskStartDateTime) {
      setElapsed(0);
      return;
    }

    const startDateTime = task.TaskStartDateTime;

    // Set initial value immediately
    setElapsed(computeElapsed(startDateTime));

    // Tick every second — recalculates from TaskStartDateTime each time (matches Xamarin Timer_Elapsed)
    intervalRef.current = setInterval(() => {
      setElapsed(computeElapsed(startDateTime));
    }, 1000);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [task?.TaskStartDateTime]);
  return elapsed;
}
